import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { isKeyboardClick, ARROW_LEFT, ARROW_UP, ARROW_RIGHT, ARROW_DOWN } from '../../utils/keyboard.js';
import { arraysEqual, shallowCompareKeys } from '../../utils/objects.js';
import { getActiveElement } from '../../utils/dom.js';
import { isElementOfType } from '../../utils/react.js';
import usePrevious from '../../hooks/usePrevious.js';
import { CommonClasses } from '../../commonClasses.js';
import Tab from './Tab.js';
import TabTitle, { generateTabTitleId, generateTabPanelId } from './TabTitle.js';
import { TabClasses } from './utils.js';

const Expander = () => /*#__PURE__*/React__default.createElement("div", {
  className: "nui-flex-expander"
});
const TAB_SELECTOR = `.${TabClasses.TAB}`;
function isTabElement(child) {
  return isElementOfType(child, Tab);
}
const BASE_CLASS = 'nui-tabs';
const getKeyCodeDirection = e => {
  if ([ARROW_LEFT, ARROW_UP].includes(e.key)) {
    return -1;
  }
  if ([ARROW_RIGHT, ARROW_DOWN].includes(e.key)) {
    return 1;
  }
  return undefined;
};
const tabsPropTypes = {
  /** Whether the selected tab indicator should animate its movement. */
  animate: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  /**
   * Initial selected tab `id`, for uncontrolled usage.
   * Note that this prop refers only to `<Tab>` children; other types of elements are ignored.
   */
  defaultSelectedTabId: PropTypes.string,
  fill: PropTypes.bool,
  /**
   * Unique identifier for this `Tabs` container. This will be combined with the `id` of each
   * `Tab` child to generate ARIA accessibility attributes. IDs are required and should be
   * unique on the page to support server-side rendering.
   */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** If set to `true`, the color scheme will be inverted and the selected tab will be indicated
   * by background-color instead of the bottom indicator shadow
   */
  inverted: PropTypes.bool,
  /**
   * If set to `true`, the tab titles will display with larger styling.
   * This will apply large styles only to the tabs at this level, not to nested tabs.
   */
  large: PropTypes.bool,
  listClassName: PropTypes.string,
  /** Whether the selection indicator should be repositioned when the window is resized */
  repositionIndicatorOnResize: PropTypes.bool,
  /** A callback function that is invoked when a tab in the tab list is clicked. */
  onChange: PropTypes.func,
  /**
   * Whether inactive tab panels should be removed from the DOM and unmounted in React.
   * This can be a performance enhancement when rendering many complex panels, but requires
   * careful support for unmounting and remounting.
  */
  renderActiveTabPanelOnly: PropTypes.bool,
  /**
   * Selected tab `id`, for controlled usage.
   * Providing this prop will put the component in controlled mode.
   * Unknown ids will result in empty selection (no errors).
   */
  selectedTabId: PropTypes.string,
  /** Whether to show tabs stacked vertically on the left side. */
  vertical: PropTypes.bool
};
const Tabs = props => {
  const {
    defaultSelectedTabId,
    selectedTabId: selectedTabIdProp
  } = props;
  const prevProps = usePrevious(props);
  const tablistElement = useRef(null);
  const resizeTimer = useRef(null);

  /** Filters children to only `<Tab>`s */
  const getTabChildren = (propsObject = props) => React__default.Children.toArray(propsObject.children).filter(isTabElement);
  const getTabChildrenProps = (propsObject = props) => getTabChildren(propsObject).map(child => child.props);
  const getInitialSelectedTabId = () => {
    // NOTE: providing an unknown ID will hide the selection
    if (selectedTabIdProp !== undefined) {
      return selectedTabIdProp;
    }
    if (defaultSelectedTabId !== undefined) {
      return defaultSelectedTabId;
    }

    // select first tab in absence of user input
    const tabs = getTabChildren();
    return tabs.length === 0 ? undefined : tabs[0].props.id;
  };
  const [selectedTabId, setSelectedTabId] = useState(getInitialSelectedTabId);
  const [indicatorWrapperStyle, setIndicatorWrapperStyle] = useState({});
  const prevSelectedTabId = usePrevious(selectedTabId);

  /**
     * Calculate the new height, width, and position of the tab indicator.
     * Store the CSS values so the transition animation can start.
     */
  const moveSelectionIndicator = (animate = true) => {
    if (tablistElement.current == null || props.inverted || !props.animate) {
      return;
    }
    const tabIdSelector = `${TAB_SELECTOR}[data-tab-id="${selectedTabId}"]`;
    const selectedTabElement = tablistElement.current.querySelector(tabIdSelector);
    let nextIndicatorWrapperStyle = {
      display: 'none'
    };
    if (selectedTabElement != null) {
      const {
        clientHeight,
        clientWidth,
        offsetLeft,
        offsetTop
      } = selectedTabElement;
      nextIndicatorWrapperStyle = {
        height: clientHeight,
        transform: `translateX(${Math.floor(offsetLeft)}px) translateY(${Math.floor(offsetTop)}px)`,
        width: clientWidth
      };
      if (!animate) {
        nextIndicatorWrapperStyle.transition = 'none';
      }
    }
    requestAnimationFrame(() => {
      setIndicatorWrapperStyle(nextIndicatorWrapperStyle);
    });
  };
  const moveSelectionIndicatorRef = useRef(moveSelectionIndicator);
  useEffect(() => {
    moveSelectionIndicator(false);
    return () => {
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
    };
  }, []);
  useEffect(() => {
    moveSelectionIndicatorRef.current = moveSelectionIndicator;
  }, [moveSelectionIndicator]);
  useEffect(() => {
    if (selectedTabIdProp !== undefined) {
      // keep state in sync with controlled prop, so state is canonical source of truth
      setSelectedTabId(selectedTabIdProp);
    }
  }, [selectedTabIdProp]);
  useEffect(() => {
    if (prevSelectedTabId && selectedTabId !== prevSelectedTabId) {
      moveSelectionIndicator();
    } else if (prevSelectedTabId != null) {
      // comparing React nodes is difficult to do with simple logic, so
      // shallowly compare just their props as a workaround.
      const didChildrenChange = !arraysEqual(getTabChildrenProps(prevProps), getTabChildrenProps(), shallowCompareKeys);
      if (didChildrenChange) {
        moveSelectionIndicator();
      }
    }
  });
  useEffect(() => {
    if (!props.repositionIndicatorOnResize) return undefined;
    const fn = () => {
      if (resizeTimer.current) {
        clearTimeout(resizeTimer.current);
      }
      setTimeout(() => {
        moveSelectionIndicatorRef.current?.();
      }, 200);
    };
    window.addEventListener('resize', fn);
    return () => {
      window.removeEventListener('resize', fn);
    };
  }, [props.repositionIndicatorOnResize]);
  const handleTabClick = (newTabId, event) => {
    props.onChange?.(newTabId, selectedTabId, event);
    if (selectedTabIdProp === undefined) {
      setSelectedTabId(newTabId);
    }
  };

  /** Queries root HTML element for all tabs with optional filter selector */
  const getTabElements = (subselector = '') => {
    if (tablistElement.current == null) {
      return [];
    }
    return Array.from(tablistElement.current.querySelectorAll(TAB_SELECTOR + subselector));
  };
  const handleKeyDown = e => {
    const targetTabElement = e.target.closest(TAB_SELECTOR);
    if (targetTabElement != null && isKeyboardClick(e)) {
      e.preventDefault();
      targetTabElement.click();
      return;
    }
    const focusedElement = getActiveElement(tablistElement.current)?.closest(TAB_SELECTOR);
    if (focusedElement == null) {
      return;
    }

    // must rely on DOM state because we have no way of mapping `focusedElement` to a JSX.Element
    const enabledTabElements = getTabElements().filter(el => el.getAttribute('aria-disabled') === 'false');
    const focusedIndex = enabledTabElements.indexOf(focusedElement);
    const direction = getKeyCodeDirection(e);
    if (focusedIndex >= 0 && direction !== undefined) {
      e.preventDefault();
      const {
        length
      } = enabledTabElements;
      // auto-wrapping at 0 and `length`
      const nextFocusedIndex = (focusedIndex + direction + length) % length;
      enabledTabElements[nextFocusedIndex].focus();
    }
  };
  const renderTabPanel = tab => {
    const {
      panel,
      id,
      panelClassName
    } = tab.props;
    if (panel === undefined) {
      return undefined;
    }
    return /*#__PURE__*/React__default.createElement("div", {
      "aria-labelledby": generateTabTitleId(props.id, id),
      "aria-hidden": id !== selectedTabId,
      className: cx(TabClasses.TAB_PANEL, panelClassName),
      id: generateTabPanelId(props.id, id),
      key: id,
      role: "tabpanel"
    }, panel);
  };
  const renderTabTitle = child => {
    if (isTabElement(child)) {
      const {
        id
      } = child.props;
      return /*#__PURE__*/React__default.createElement(TabTitle, _extends({}, child.props, {
        parentId: props.id,
        onClick: handleTabClick,
        selected: id === selectedTabId
      }));
    }
    return child;
  };
  const tabTitles = React__default.Children.map(props.children, renderTabTitle);
  const tabPanels = getTabChildren().filter(props.renderActiveTabPanelOnly ? tab => tab.props.id === selectedTabId : () => true).map(renderTabPanel);
  const tabIndicator = props.animate && !props.inverted ? /*#__PURE__*/React__default.createElement("div", {
    className: TabClasses.TAB_INDICATOR_WRAPPER,
    style: indicatorWrapperStyle
  }, /*#__PURE__*/React__default.createElement("div", {
    className: TabClasses.TAB_INDICATOR
  })) : null;
  const classes = cx(BASE_CLASS, props.className, {
    [CommonClasses.VERTICAL]: props.vertical,
    [CommonClasses.FILL]: props.fill,
    [CommonClasses.INVERTED]: props.inverted
  });
  const tabListClasses = cx(TabClasses.TAB_LIST, props.listClassName, {
    [CommonClasses.LARGE]: props.large,
    [TabClasses.TAB_LIST_SINGLE]: getTabChildren().length === 1
  });
  return /*#__PURE__*/React__default.createElement("div", {
    className: classes
  }, /*#__PURE__*/React__default.createElement("div", {
    className: tabListClasses,
    onKeyDown: handleKeyDown,
    ref: tablistElement,
    role: "tablist"
  }, tabIndicator, tabTitles), tabPanels);
};

/** Insert a `Tabs.Expander` between any two children to right-align all subsequent children. */
Tabs.Expander = Expander;
Tabs.Tab = Tab;
Tabs.propTypes = tabsPropTypes;
Tabs.defaultProps = {
  animate: true,
  className: undefined,
  defaultSelectedTabId: undefined,
  fill: false,
  inverted: false,
  large: false,
  onChange: null,
  renderActiveTabPanelOnly: false,
  repositionIndicatorOnResize: false,
  selectedTabId: undefined,
  vertical: false
};
Tabs.displayName = 'NuiTabs';
var Tabs$1 = Tabs;

export { Expander, Tabs$1 as default };
